// The shop's customers kept on this device (IndexedDB), so phone lookup and
// name search also work without internet. The first time (and once a week)
// the whole list is downloaded; after that only what changed, every 3 hours.
// Deleted on logout.
import { offlineDb } from "./offlineDb";
import orderService from "../orders/index1";

const SYNC_KEY = "pos_customers_sync";
const UPDATE_EVERY_MS = 3 * 60 * 60 * 1000;
const FULL_EVERY_MS = 7 * 24 * 60 * 60 * 1000;
const KEEP = 3;

const lastTen = (phone) =>
  String(phone || "")
    .replace(/\D/g, "")
    .slice(-10);

// the list in memory for fast name search (read from the database once)
let memory = null;
let memoryShop = null;
let syncing = null;

// saved with the customers (one step), so a crash never leaves them out of step
const readSync = async () => {
  try {
    return (await offlineDb.meta.get(SYNC_KEY))?.value || null;
  } catch (error) {
    return null;
  }
};

// customers of another shop are never shown
const ownShop = (state) =>
  !!state?.shopId && String(state.shopId) === localStorage.getItem("shopId");

const unique = (list) =>
  list
    .map((v) => String(v || "").trim())
    .filter(
      (v, i, all) =>
        v && all.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i,
    )
    .slice(0, KEEP);

// newer information on top of what the device already has
const merged = (old, fresh, addOrders) => ({
  key: lastTen(fresh.phone_number),
  name: String(fresh.name || old?.name || "").trim(),
  // the saved account's phone format stays (POS delivery finds the same customer)
  phone_number: old?.phone_number || fresh.phone_number,
  addresses: unique([...(fresh.addresses || []), ...(old?.addresses || [])]),
  carNumbers: unique([...(fresh.carNumbers || []), ...(old?.carNumbers || [])]),
  lastDeliveryFee: fresh.lastDeliveryFee ?? old?.lastDeliveryFee ?? null,
  orderCount: addOrders
    ? (old?.orderCount || 0) + (fresh.orders || 0)
    : (fresh.orders ?? fresh.orderCount ?? old?.orderCount ?? 0),
  lastOrderAt:
    [fresh.lastOrderAt, old?.lastOrderAt].filter(Boolean).sort().reverse()[0] ||
    null,
});

const saveAll = async (records) => {
  await offlineDb.customers.bulkPut(records);
  memory = null;
};

// customers and the update state in one step: all saved or nothing
const saveSync = async (records, state, clearFirst) => {
  await offlineDb.transaction(
    "rw",
    offlineDb.customers,
    offlineDb.meta,
    async () => {
      if (clearFirst) await offlineDb.customers.clear();
      await offlineDb.customers.bulkPut(records);
      await offlineDb.meta.put({ key: SYNC_KEY, value: state });
    },
  );
  memory = null;
};

// Online only, as often as you like: asks the server only when an update is due.
export const syncCustomers = async (shopId) => {
  if (!shopId) return;
  if (syncing) return syncing;
  const state = await readSync();
  const now = Date.now();
  const sameShop = state?.shopId === shopId;
  const full =
    !sameShop || !state?.fullAt || now - state.fullAt > FULL_EVERY_MS;
  if (!full && now - (state.checkedAt || 0) < UPDATE_EVERY_MS) return;
  syncing = (async () => {
    try {
      const res = await orderService.fetchOfflineCustomers(
        full ? undefined : state.at,
      );
      const data = res?.data;
      if (!data?.customers) return;
      const fresh = data.customers.filter(
        (c) => lastTen(c.phone_number).length === 10,
      );
      const next = {
        shopId,
        at: data.at,
        checkedAt: now,
        fullAt: data.full ? now : state?.fullAt,
      };
      if (data.full) {
        await saveSync(
          fresh.map((c) => merged(null, c, false)),
          next,
          true,
        );
      } else {
        const old = await offlineDb.customers.bulkGet(
          fresh.map((c) => lastTen(c.phone_number)),
        );
        // order counts are added on top: saved with the state, so never twice
        await saveSync(
          fresh.map((c, i) => merged(old[i], c, true)),
          next,
          false,
        );
      }
    } catch (error) {
      // no internet or no permission: the list on the device stays as it is
    } finally {
      syncing = null;
    }
  })();
  return syncing;
};

// a customer just found or sold to: on the device at once (before the next update)
export const rememberCustomer = (customer) => {
  const key = lastTen(customer?.phone_number);
  if (key.length < 10) return;
  offlineDb.customers
    .get(key)
    .then((old) =>
      saveAll([
        merged(old, { ...customer, orders: customer.orderCount }, false),
      ]),
    )
    .catch(() => {});
};

// by the last 10 digits, like the server lookup
export const findCustomerByPhone = async (phone) => {
  const key = lastTen(phone);
  if (key.length < 10) return null;
  if (!ownShop(await readSync())) return null;
  const found = await offlineDb.customers.get(key).catch(() => null);
  return found ? { ...found, local: true } : null;
};

// a word of the name starts with what is typed; latest visit first
export const searchCustomersByName = async (text, limit = 8) => {
  const words = String(text || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return [];
  const shop = localStorage.getItem("shopId");
  if (!memory || memoryShop !== shop) {
    memory = ownShop(await readSync())
      ? await offlineDb.customers.toArray().catch(() => [])
      : [];
    memoryShop = shop;
  }
  return memory
    .filter((c) => {
      const nameWords = String(c.name || "")
        .toLowerCase()
        .split(/\s+/);
      return words.every((w) => nameWords.some((n) => n.startsWith(w)));
    })
    .sort((a, b) =>
      String(b.lastOrderAt || "").localeCompare(String(a.lastOrderAt || "")),
    )
    .slice(0, limit)
    .map((c) => ({ ...c, local: true }));
};

// logout or session end: no customer data stays on the device
export const clearDeviceCustomers = async () => {
  memory = null;
  localStorage.removeItem(SYNC_KEY);
  await offlineDb
    .transaction("rw", offlineDb.customers, offlineDb.meta, () =>
      Promise.all([offlineDb.customers.clear(), offlineDb.meta.clear()]),
    )
    .catch(() => {});
};
